<template>
  <div class="sip-ind" :class="statusClass" :title="title" role="status" :aria-label="title">
    <PhoneIcon :size="16" />
    <span class="sip-ind__dot" aria-hidden="true" />
  </div>
</template>

<script setup lang="ts">
/**
 * Компактный индикатор статуса SIP в топбаре (DEV-425).
 *
 * Заменяет плавающий бейдж в правом-нижнем углу (мешал контенту, например кнопке
 * перемотки канбана). Подключением SIP по-прежнему владеет ActiveCallWidget —
 * здесь только читаем общий (модульный) реактивный статус из useSipClient.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { PhoneIcon } from 'lucide-vue-next'
import { useSipClient } from '@/features/telephony/composables/useSipClient'

const { t } = useI18n()
const sip = useSipClient()

const statusClass = computed(() => ({
  'sip-ind--registered': sip.status.value === 'registered',
  'sip-ind--connecting': sip.status.value === 'connecting',
  'sip-ind--error': sip.status.value === 'error',
  'sip-ind--disconnected': sip.status.value === 'disconnected',
}))

const title = computed(() => {
  switch (sip.status.value) {
    case 'registered':
      return t('telephony.sip.connected')
    case 'connecting':
      return t('telephony.sip.connecting')
    case 'error':
      return t('telephony.sip.error', { error: sip.error.value || 'unknown' })
    default:
      return t('telephony.sip.disconnected')
  }
})
</script>

<style scoped>
.sip-ind {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md, 10px);
  color: var(--color-text-muted);
}

.sip-ind__dot {
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-text-muted);
  border: 2px solid var(--color-background-elevated);
}

.sip-ind--registered {
  color: var(--color-success);
}
.sip-ind--registered .sip-ind__dot {
  background: var(--color-success);
}

.sip-ind--connecting {
  color: var(--color-warning);
}
.sip-ind--connecting .sip-ind__dot {
  background: var(--color-warning);
  animation: sip-ind-pulse 1.6s ease-in-out infinite;
}

.sip-ind--error {
  color: var(--color-danger);
}
.sip-ind--error .sip-ind__dot {
  background: var(--color-danger);
}

.sip-ind--disconnected {
  color: var(--color-text-muted);
}

@keyframes sip-ind-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}
</style>
